import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { AVAILABILITY_LABEL, agents as agentsApi, tiersOf } from "@/api/agents";
import { useSession } from "@/auth/session";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/page";
import { useSocketBinding } from "@/realtime/use-binding";
import { useMissingModules } from "@/shell/capabilities";
import { AvailabilityMenu } from "./availability";
import { agentDot } from "./model";
import { useInvalidateAgent, useQueueList } from "./queries";

/**
 * "My status" in the top bar, for the person signed in rather than the
 * account being looked at: an agent moves between Available, On demand,
 * On break and Logged out from wherever they are in the console.
 *
 * It is shown when the signed-in user is an agent — that is, when their
 * user document carries a `call_center` object, which is exactly when
 * `GET .../agents/{id}` answers — and not on a role, because roles are a
 * later phase.
 */
export function MyStatus() {
  const session = useSession((s) => s.session);
  const accountId = session?.accountId;
  const ownerId = session?.ownerId;
  const missing = useMissingModules();
  const enabled = Boolean(accountId && ownerId) && !missing.has("agents");
  const qc = useQueryClient();
  const invalidateAgent = useInvalidateAgent(accountId);

  const agent = useQuery({
    queryKey: ["agent", accountId, ownerId],
    queryFn: () => agentsApi.get(accountId!, ownerId!),
    enabled,
    // A 404 means the user is not an agent, which is an answer, not a fault.
    retry: false,
    staleTime: 60_000,
  });

  // A feature code on the phone changes the same value, so the control
  // follows it rather than waiting for the next visit.
  const onEvent = React.useCallback(
    (ev: { data: Record<string, unknown> }) => {
      if (ev.data?.agent_id !== ownerId) return;
      void qc.invalidateQueries({ queryKey: ["agent", accountId, ownerId] });
    },
    [qc, accountId, ownerId],
  );
  useSocketBinding(enabled && agent.data ? accountId : undefined, "callcenter.agent.*", onEvent);

  const queues = useQueueList(agent.data ? accountId : undefined);
  const queueNames = React.useMemo(() => new Map((queues.data?.items ?? []).map((q) => [q.id, q.name ?? q.id])), [queues.data]);

  if (!enabled || !agent.data) return null;
  const mine = agent.data;
  const memberships = tiersOf(mine).map((t) => t.queue_name ?? queueNames.get(t.queue_id) ?? t.queue_id);

  return (
    <AvailabilityMenu accountId={accountId!} agentId={ownerId!} current={mine.availability} userDisabled={mine.enabled === false} onChanged={() => invalidateAgent(ownerId)}>
      <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" aria-label="My call center status">
        <StatusDot state={agentDot(mine.availability, mine.activity)} />
        <span className="hidden sm:inline">{mine.availability ? AVAILABILITY_LABEL[mine.availability] : "Not logged in"}</span>
        {memberships.length > 0 && <span className="hidden text-2xs text-muted-foreground md:inline">{memberships.join(", ")}</span>}
        <ChevronDown className="size-3" />
      </Button>
    </AvailabilityMenu>
  );
}
